import role3Schema from '../../../../assets/forms/role-3.form.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';

/**
 * Pure validator for Role 3 (Stock Exchange Disclosure).
 *
 * Mirrors the editor's role3-110000-stock-exchange.component.ts validation
 * surface — i.e. what drives `stockExchangeForm.valid`:
 *
 *  - Static required: fields where buildForm() attaches Validators.required —
 *    i.e. unconditionally required via EITHER `control.required === true` OR
 *    a `validators: [{ name: 'required' }]` entry without conditionalOn.
 *    (Same latent-bug-safe pattern used by Role 6; see
 *    role6-director-business-review.validator.ts for the full rationale.)
 *
 * Intentionally NOT enforced here (parity preservation):
 *  - `requiredIf` entries in the schema. The role-3 component explicitly
 *    returns `false` from `isFieldRequired()` for every control and never
 *    attaches required validators for `requiredIf` triggers — the companion
 *    visibility logic (`updateConditionalField`) only calls
 *    `updateValueAndValidity()` with no validator changes. Honoring
 *    `requiredIf` here would raise errors the mount-based path never raises
 *    and break the parity harness in dev.
 *  - `warnings` entries ARE now enforced for `hasSuggestedChanges` (not for
 *    `hasErrors`). All role-3 warnings (isEmpty, conditionalRequired,
 *    dateComparison) are evaluated via the shared `sheetHasTriggeredWarning`
 *    helper — the DI-free twin of the editor's FieldWarningService — and OR'd
 *    into `hasSuggestedChanges`. The editor's status closure rolls the same
 *    warnings up too, so the red asterisk and the submitted flag agree. This
 *    leaves `hasErrors` untouched (warnings are non-blocking).
 *  - `maxLength` on schema controls. The component only uses maxLength for
 *    template rendering (getFieldMaxLength) and never wires a
 *    Validators.maxLength into the FormControl, so it does not affect
 *    form.valid.
 *
 * Net effect today: role-3 schema declares zero unconditionally required
 * fields, so this validator returns `hasErrors: false` for every input. The
 * schema-scanning machinery is retained so that if a future schema edit
 * flips `control.required` to `true` (or adds a `validators[name=='required']`
 * entry) the validator picks it up automatically without another lift — and
 * the parity harness will catch any accidental divergence from the editor.
 *
 * Tier classification: 2 per the Phase 2 plan. The sheet historically carried
 * same-sheet requiredIf / conditional-visibility metadata; none of it ever drove
 * `form.valid`, and as of 2026-08-04 the last `conditional` block is gone (all
 * nine fields always render). The tier is unchanged because the sheet still
 * reads `filingPrerequisites` / `filingInfo` cross-sheet for its [#f7]/[#f8]/[#f9]
 * warnings — a read that feeds `hasSuggestedChanges` only, not validity, so it
 * does not graduate to Tier 3.
 */

interface SchemaValidator {
  name?: string;
  conditionalOn?: unknown;
}

interface SchemaControl {
  qname: string;
  required?: boolean;
  validators?: SchemaValidator[];
}

const SCHEMA_CONTROLS: ReadonlyArray<SchemaControl> =
  role3Schema.controls as SchemaControl[];

function hasConditionalRequirement(control: SchemaControl): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn !== undefined
    ) ?? false
  );
}

function hasUnconditionalRequiredViaValidators(control: SchemaControl): boolean {
  return (
    control.validators?.some(
      (validator) => validator.name === 'required' && validator.conditionalOn === undefined
    ) ?? false
  );
}

/**
 * Static required keys — fields where buildForm() adds Validators.required.
 * Precomputed at module load since the rule is pure schema-driven.
 *
 * A field is unconditionally required when EITHER `control.required === true`
 * OR the validators array contains a `{ name: 'required' }` entry without a
 * conditionalOn property (the Role 6 latent-bug-safe pattern).
 */
const STATIC_REQUIRED_KEYS: ReadonlySet<string> = new Set(
  SCHEMA_CONTROLS.filter((control) => {
    const isUnconditional =
      control.required === true || hasUnconditionalRequiredViaValidators(control);
    return isUnconditional && !hasConditionalRequirement(control);
  }).map((control) => control.qname)
);

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function checkRequiredErrors(sheetData: Record<string, any>): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const key of STATIC_REQUIRED_KEYS) {
    if (isEmpty(sheetData[key])) {
      errors[key] = `${key} is required`;
    }
  }
  return errors;
}

export const role3StockExchangeValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.stockExchangeDisclosure,
  tier: 2,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    const errors: Record<string, string> = {
      ...checkRequiredErrors(sheetData),
    };
    // Roll the sheet's schema-declared `warnings` (isEmpty / conditionalRequired
    // / dateComparison) up into hasSuggestedChanges, mirroring the editor's
    // FieldWarningService surface. Filing-info comes from crossSheetData (aliased
    // under both keys by buildContext), so the bag matches the editor's
    // `{ ...buildFilingInfoBagEntry(), ...currentFormValues }`.
    const filingInfo =
      context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
      context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
      {};
    const warningBag = buildWarningBag(filingInfo, sheetData);
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role3Schema.controls as unknown as WarnableControl[],
        sheetData,
        warningBag
      ),
      errors,
    };
  },
};
